import React from "react";
import { FlashpackType } from "./api";

export function ageRange(t, age_min, age_max) {
  return age_max
    ? age_min === age_max
      ? t("age_single", { age: age_min })
      : t("age_finite_range", { from: age_min, to: age_max })
    : t("age_unbounded_range", { from: age_min });
}

const NO_VALUE = {};

export const CLEVER_REDIRECT_PATH = "/account/login/clever/";
export const CLASSLINK_REDIRECT_PATH = "/account/login/classlink/";
export const CLASSLINK_DEFAULT_DEEP_LINK_PATH =
  "/student/login/classlink/default/";

// This regex is defined in Android
// Android/sdk/sources/android-34/android/util/Patterns.java
export const EMAIL_ADDRESS_REGEX =
  /[a-zA-Z0-9+._%+-]{1,256}@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,25})+/;

export class LiveData<T> {
  public value: T;
  private isInitialized: boolean;
  private observers: ((data: T) => void)[] = [];

  constructor(initialValue: T | {} = NO_VALUE) {
    if (initialValue !== NO_VALUE) {
      this.value = initialValue as T;
      this.isInitialized = true;
    } else {
      this.isInitialized = false;
    }
    this.notifyObservers();
  }

  public setValue(newValue: T): void {
    this.value = newValue;
    this.isInitialized = true;
    this.notifyObservers();
  }

  public observe(observer: (data: T) => void): () => void {
    this.observers.push(observer);
    this.notifyObservers();
    return () => {
      this.removeObserver(observer);
    };
  }

  public removeObserver(observer: (data: T) => void): void {
    const index = this.observers.indexOf(observer);
    if (index > -1) {
      this.observers.splice(index, 1);
    }
  }

  private notifyObservers(): void {
    if (this.isInitialized) {
      this.observers.forEach((observer) => observer(this.value!));
    }
  }
}

export const useLiveData = function <T>(
  callback: (value: T) => void,
  liveData: LiveData<T>
) {
  React.useEffect(() => {
    return liveData.observe((value) => {
      callback(value);
    });
  }, [liveData, callback]);
};

export const useMediaQuery = (query: string) => {
  const [matches, setMatches] = React.useState(
    () => window.matchMedia(query).matches
  );

  React.useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const onChange = () => setMatches(mediaQueryList.matches);
    onChange();
    mediaQueryList.addEventListener("change", onChange);
    return () => mediaQueryList.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

const DRAG_TO_SCROLL_THRESHOLD = 4;

export const useDragToScroll = () => {
  const drag = React.useRef<{
    startX: number;
    startScrollLeft: number;
    isDragging: boolean;
  }>();
  const didDrag = React.useRef(false);
  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse" || event.button !== 0) {
      return;
    }
    didDrag.current = false;
    drag.current = {
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      isDragging: false,
    };
  };
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!drag.current) {
      return;
    }
    const deltaX = event.clientX - drag.current.startX;
    if (!drag.current.isDragging) {
      if (Math.abs(deltaX) < DRAG_TO_SCROLL_THRESHOLD) {
        return;
      }
      drag.current.isDragging = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.classList.add("is-dragging");
    }
    event.currentTarget.scrollLeft = drag.current.startScrollLeft - deltaX;
  };
  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    didDrag.current = !!drag.current?.isDragging;
    drag.current = undefined;
    event.currentTarget.classList.remove("is-dragging");
  };
  const onClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    if (didDrag.current) {
      didDrag.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };
  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onClickCapture,
    onDragStart: (event: React.DragEvent<HTMLElement>) =>
      event.preventDefault(),
  };
};

// Calls onResize on the next frame after any of the elements resizes, and
// returns a cleanup function. Measuring inside the ResizeObserver callback
// would set React state synchronously, and if that re-render resizes an
// observed element the browser reports "ResizeObserver loop completed with
// undelivered notifications".
export const observeResize = (elements: Element[], onResize: () => void) => {
  let rafId: number | null = null;
  const observer = new ResizeObserver(() => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(onResize);
  });
  elements.forEach((element) => observer.observe(element));
  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    observer.disconnect();
  };
};

export const useHasHorizontalOverflow = (element: HTMLElement | null) => {
  const [hasOverflow, setHasOverflow] = React.useState(false);

  React.useEffect(() => {
    if (!element) {
      return;
    }
    const measure = () => {
      const first = element.firstElementChild as HTMLElement | null;
      const last = element.lastElementChild as HTMLElement | null;
      const contentWidth =
        first && last
          ? last.offsetLeft + last.offsetWidth - first.offsetLeft
          : 0;
      setHasOverflow(contentWidth > element.clientWidth);
    };
    const stopObservingResize = observeResize([element], measure);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(element, { childList: true });
    return () => {
      stopObservingResize();
      mutationObserver.disconnect();
    };
  }, [element]);

  return hasOverflow;
};

export const useGridColumnCount = (
  element: HTMLElement | null,
  columnWidth: number,
  columnGap?: number,
  { fit = "min" }: { fit?: "min" | "max" } = {}
) => {
  const [columnCount, setColumnCount] = React.useState(0);

  React.useEffect(() => {
    if (!element) {
      return;
    }
    const measure = () => {
      const gap =
        columnGap ?? (parseFloat(getComputedStyle(element).columnGap) || 0);
      const round = fit === "max" ? Math.ceil : Math.floor;
      setColumnCount(
        Math.max(1, round((element.clientWidth + gap) / (columnWidth + gap)))
      );
    };
    measure();
    return observeResize([element], measure);
  }, [element, columnWidth, columnGap, fit]);

  return columnCount;
};

export const stringOrNullToNumberOrNull = (value: string | null) =>
  value == null ? value : +value;

export function packLessonIdAndCustomFlashpackId(
  lessonId: number,
  customFlashpackId: number | null
) {
  return lessonId + "_" + (customFlashpackId || "");
}

export function unpackLessonIdAndCustomFlashpackId(
  lessonAndCustomFlashpackId: string
): [number, number | null] {
  let [first, second] = lessonAndCustomFlashpackId.split("_");
  return [parseInt(first), second ? parseInt(second) : null];
}

// Math operators in their display form. The webapp shows these and stores them
// directly in the flashpack items (e.g. "4 × 5"); the math backend also accepts
// ASCII alternatives (x, *, /) in creator-studio decks but always serves and
// stores custom-pack items in display form.
const MATH_OPERATORS: { [key: number]: string } = {
  [FlashpackType.Addition]: "+",
  [FlashpackType.Subtraction]: "-",
  [FlashpackType.Multiplication]: "×",
  [FlashpackType.Division]: "÷",
};

// Characters a student might type for each operator, including the ASCII
// alternatives (x, *, /) for the display operators × and ÷ that aren't on a
// keyboard.
const OPERATOR_BY_TYPED_CHARACTER: { [character: string]: string } = {
  "+": "+",
  "-": "-",
  "×": "×",
  x: "×",
  X: "×",
  "*": "×",
  "÷": "÷",
  "/": "÷",
};

// The display operator for a typed character, or null if it isn't an operator.
export const normalizeMathOperator = (character: string): string | null =>
  OPERATOR_BY_TYPED_CHARACTER[character] ?? null;

export const isMathFlashpackType = (flashpackType: FlashpackType) =>
  flashpackType === FlashpackType.Addition ||
  flashpackType === FlashpackType.Subtraction ||
  flashpackType === FlashpackType.Multiplication ||
  flashpackType === FlashpackType.Division ||
  flashpackType === FlashpackType.Math;

// Operators selectable for a flashpack type. A single-operation type yields its
// one fixed operator; the umbrella Math type lets the student pick any of them.
export const getMathOperators = (flashpackType: FlashpackType): string[] =>
  flashpackType === FlashpackType.Math
    ? Object.values(MATH_OPERATORS)
    : [MATH_OPERATORS[flashpackType]];

export const getFlashpackAddItemTitle = (t, flashpackType) => {
  return flashpackType === FlashpackType.SightWords
    ? t("flashpacks_items_title_sight_words")
    : flashpackType === FlashpackType.Spelling
    ? t("flashpacks_items_title_spelling")
    : isMathFlashpackType(flashpackType)
    ? t("flashpacks_items_title_math")
    : t("flashpacks_items_title_generic");
};
